import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useT } from '../i18n';
import { getApiClient } from '../api/factory';
import {
  createDepartment,
  deleteDepartment,
  listDepartments,
  updateDepartment,
  type Department,
} from '../api/departments';
import { ApiError } from '../api/client';

// ANX-072 — Админская секция в /settings: CRUD отделов организации (tenant).
// Видна только operator с role=admin (паттерн ConversationTagsAdmin).
// Отделы используются в pre-chat форме виджета (см. PreChatBlock в SitesPage).

export function DepartmentsAdmin() {
  const role = useAuthStore((s) => s.session?.operator.role);
  const t = useT();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: departments } = useQuery({
    queryKey: ['departments'] as const,
    queryFn: () => listDepartments(getApiClient()),
    enabled: role === 'admin',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['departments'] });
  };

  const createMut = useMutation({
    mutationFn: (input: { name: string }) => createDepartment(getApiClient(), input),
    onSuccess: () => {
      setName('');
      setError(null);
      invalidate();
    },
  });

  const updateMut = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      updateDepartment(getApiClient(), input.id, { name: input.name }),
    onSuccess: () => {
      setEditingId(null);
      setName('');
      setError(null);
      invalidate();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteDepartment(getApiClient(), id),
    onSuccess: invalidate,
  });

  if (role !== 'admin') return null;

  function startEdit(dep: Department) {
    setEditingId(dep.id);
    setName(dep.name);
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setName('');
    setError(null);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim() === '') {
      setError(t('departments.error.nameRequired'));
      return;
    }
    const action = editingId
      ? updateMut.mutateAsync({ id: editingId, name: name.trim() })
      : createMut.mutateAsync({ name: name.trim() });
    action.catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(t('departments.error.nameTaken'));
      } else {
        setError(t('common.unknownError'));
      }
    });
  }

  function handleDelete(dep: Department) {
    if (!window.confirm(t('departments.confirmDelete') + ' ' + dep.name + '?')) return;
    deleteMut.mutate(dep.id);
  }

  return (
    <section className="mt-6 rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{t('departments.adminTitle')}</h3>
      <p className="mb-3 text-xs text-slate-500">{t('departments.adminHint')}</p>

      <ul className="mb-4 flex flex-col gap-1.5">
        {(departments ?? []).map((dep) => (
          <li
            key={dep.id}
            className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          >
            <span className="font-medium">{dep.name}</span>
            <span className="flex gap-1">
              <button
                type="button"
                onClick={() => startEdit(dep)}
                aria-label={t('common.edit')}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                <Pencil size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(dep)}
                aria-label={t('common.delete')}
                disabled={deleteMut.isPending}
                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-red-600 disabled:opacity-40"
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </span>
          </li>
        ))}
        {departments && departments.length === 0 && (
          <li className="text-xs italic text-slate-500">{t('departments.empty')}</li>
        )}
      </ul>

      <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder={t('departments.namePlaceholder')}
          aria-label={t('departments.namePlaceholder')}
          maxLength={60}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-1 focus:ring-accent-200"
        />
        <button
          type="submit"
          disabled={createMut.isPending || updateMut.isPending}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {editingId ? t('common.save') : t('common.create')}
        </button>
        {editingId && (
          <button
            type="button"
            onClick={cancelEdit}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            {t('common.cancel')}
          </button>
        )}
      </form>
      {error && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
